import numeral from "numeral";
import moment from "moment/moment";
import momentDurationFormatSetup from "moment-duration-format";
import parseFormat from "moment-parseformat";

import date from "./date";
import { ACCEPTED_DATE_FORMATS } from "./constants";
import { FormatType, CellChangeStyle } from "./constants";

momentDurationFormatSetup(moment);

const SORTED_CURRENCIES = ["$", "€", "£", "¥", "₹", "₽", "kr", "MX$", "R$", "₩", "₪", "₫", "Rp", "S$"]
    .sort((a, b) => b.length - a.length);

const formatter = {

    formatAxis: function(value, axis, isFirst) {
        let formatOptions = axis.userOptions;
        if (!formatOptions) {
            return value;
        }
        if (formatOptions.type === "datetime") {
            return this.formatDateForAxis(value, formatOptions, isFirst);
        }
        if (isNaN(value)) {
            return value;
        }
        let currency = formatOptions.labelCurrency === "none" || formatOptions.labelCurrency === undefined ? "" : formatOptions.labelCurrency;
        value = this.scaleValue(value, formatOptions.labelFormat);
        if (axis.coll !== "xAxis") {
            value = formatOptions.labelAbbreviation && formatOptions.labelFormat !== "percent" ? this.setAbbreviation(value) : this.numberWithCommas(value, formatOptions.labelFormat);
        }
        return currency + value;
    },

    formatDateForAxis(value, options, isFirst) {
        let d = date(value).toMoment();
        let interval = options.dateInterval;
        let format = options.dateFormat;
        let simpleDate = options.dateSimple;

        let label;

        switch (interval) {
            case "daily":
                if (simpleDate) {
                    if (isFirst || d.date() === 1) {
                        label = d.format("MMM");
                    } else {
                        label = d.format("D");
                    }
                } else {
                    if (format === "monthFirst") {
                        if (isFirst || d.date() === 1) {
                            label = d.format("MMM D");
                        } else {
                            label = d.format("D");
                        }
                    } else {
                        if (isFirst || d.date() === 1) {
                            label = d.format("D MMM");
                        } else {
                            label = d.format("D");
                        }
                    }
                }
                break;
            case "monthly":
                if (isFirst || d.month() === 0) {
                    label = simpleDate ? d.format("YYYY") : d.format("MMM 'YY");
                } else {
                    label = d.format("MMM");
                }
                break;
            case "quarterly":
                if (isFirst || d.quarter() === 1) {
                    label = simpleDate ? "'" + d.format("YY") : "Q" + d.quarter() + " '" + d.format("YY");
                } else {
                    label = "Q" + d.quarter();
                }
                break;
            case "yearly":
                if (simpleDate) {
                    if (isFirst) {
                        label = d.format("YYYY");
                    } else {
                        label = "'" + d.format("YY");
                    }
                } else {
                    label = d.format("YYYY");
                }
                break;
            default:
                label = d.format("MM/DD/YY");
                break;
        }
        return label;
    },

    scaleValue(value, scale) {
        switch (scale) {
            case "percent":
                return value / 100;
            case "ones":
                return value;
            case "thousands":
                return value * 1000;
            case "millions":
                return value * 1000000;
            case "billions":
                return value * 1000000000;
            case "trillions":
                return value * 1000000000000;
            default:
                return value;
        }
    },

    capitalizeFirstLetter(string) {
        return string.charAt(0).toUpperCase() + string.slice(1);
    },

    numberWithCommas(x, labelFormat) {
        let formattedNumber;
        if (x % 1 != 0) {
            formattedNumber = numeral(x).format("0.0[000000]");
        } else {
            formattedNumber = numeral(x).format(",");
        }
        if (formattedNumber.contains(".")) {
            formattedNumber = formattedNumber.replace(/0+$/g, "");
        }

        if (labelFormat === "percent") {
            formattedNumber += "%";
        }
        return formattedNumber;
    },

    setAbbreviation(value) {
        let abs = Math.abs(value);
        let newVal;
        if (abs >= 1000000000000) {
            newVal = this.numberWithCommas(value / 1000000000000) + "T";
        } else if (abs >= 1000000000) {
            newVal = this.numberWithCommas(value / 1000000000) + "B";
        } else if (abs >= 1000000) {
            newVal = this.numberWithCommas(value / 1000000) + "M";
        } else if (abs >= 1000) {
            newVal = this.numberWithCommas(value / 1000) + "K";
        } else {
            newVal = this.numberWithCommas(value);
        }
        return newVal;
    },

    setSeparatorsSymbols(value, separators) {
        return value
            .replace(/,/g, "TEMP_THOUSANDS")
            .replace(/\./g, separators.decimal)
            .replace(/TEMP_THOUSANDS/g, separators.thousands);
    },

    getDefaultFormatOptions() {
        return {
            decimal: "auto",
            separator: true,
            abbreviated: false,
            changeStyle: CellChangeStyle.NONE,
            changeColor: true,
            currency: "$",
            dateFormat: "MMM Do",
            accountingStyle: false,
            textAlign: "center"
        };
    },

    stripNumericDecorators(value) {
        if (value == null) {
            return { value: "", isNegative: false };
        }

        let normalizedValue = String(value).trim();
        let isNegative = false;

        if (normalizedValue.startsWith("(") && normalizedValue.endsWith(")")) {
            isNegative = true;
            normalizedValue = normalizedValue.slice(1, -1).trim();
        }

        if (normalizedValue.startsWith("-")) {
            isNegative = true;
            normalizedValue = normalizedValue.slice(1).trim();
        } else if (normalizedValue.startsWith("+")) {
            normalizedValue = normalizedValue.slice(1).trim();
        }

        for (const currency of this.getCurrencies()) {
            if (normalizedValue.startsWith(currency)) {
                normalizedValue = normalizedValue.slice(currency.length).trim();
                break;
            }
        }

        for (const currency of this.getCurrencies()) {
            if (normalizedValue.endsWith(currency)) {
                normalizedValue = normalizedValue.slice(0, -currency.length).trim();
                break;
            }
        }

        if (normalizedValue.endsWith("%")) {
            normalizedValue = normalizedValue.slice(0, -1).trim();
        }

        normalizedValue = normalizedValue.replace(/\s+/g, "");

        return { value: normalizedValue, isNegative };
    },

    normalizeNumericString(value) {
        const { value: strippedValue, isNegative } = this.stripNumericDecorators(value);

        if (!strippedValue) {
            return null;
        }

        if (!/^[0-9.,]+$/.test(strippedValue)) {
            return null;
        }

        const normalizeSingleSeparator = (input, separator) => {
            const parts = input.split(separator);

            if (parts.length === 1) {
                return input;
            }

            if (separator === "." && parts.length === 2) {
                const [whole = "0", decimal = ""] = parts;
                return `${whole || "0"}.${decimal}`;
            }

            if (parts.length === 2) {
                const [whole = "0", decimal = ""] = parts;
                if (separator === "," && decimal.length === 3 && whole !== "") {
                    return `${whole}${decimal}`;
                }

                return `${whole || "0"}.${decimal}`;
            }

            const middleGroups = parts.slice(1, -1);
            const lastGroup = parts[parts.length - 1];
            const groupedThousands = middleGroups.every(part => part.length === 3);

            if (groupedThousands && lastGroup.length === 3) {
                return parts.join("");
            }

            if (separator === "," && groupedThousands) {
                return `${parts.slice(0, -1).join("")}.${lastGroup}`;
            }

            return parts.join("");
        };

        let normalizedValue = strippedValue;
        const hasComma = normalizedValue.includes(",");
        const hasDot = normalizedValue.includes(".");

        if (hasComma && hasDot) {
            const lastComma = normalizedValue.lastIndexOf(",");
            const lastDot = normalizedValue.lastIndexOf(".");
            const decimalSeparator = lastComma > lastDot ? "," : ".";
            const thousandsSeparator = decimalSeparator === "," ? "." : ",";

            normalizedValue = normalizedValue
                .split(thousandsSeparator).join("")
                .replace(decimalSeparator, ".");
        } else if (hasComma) {
            normalizedValue = normalizeSingleSeparator(normalizedValue, ",");
        } else if ((normalizedValue.match(/\./g) || []).length > 1) {
            normalizedValue = normalizeSingleSeparator(normalizedValue, ".");
        }

        if (!/^\d*\.?\d+$/.test(normalizedValue)) {
            return null;
        }

        return `${isNegative ? "-" : ""}${normalizedValue}`;
    },

    parseNumberString(value) {
        if (typeof value === "number") {
            return Number.isFinite(value) ? value : null;
        }

        const normalizedValue = this.normalizeNumericString(value);
        if (normalizedValue == null) {
            return null;
        }

        const parsedValue = Number(normalizedValue);
        return Number.isFinite(parsedValue) ? parsedValue : null;
    },

    formatValue(value, format, formatOptions) {
        if (!formatOptions) {
            formatOptions = this.getDefaultFormatOptions();
        }

        switch (format) {
            case FormatType.PERCENT: {
                // numeral doesn't support percentages above 1, hence divide
                // divide by 100 to get the percentage
                // if (value > 1) value = value / 100;
                if (formatOptions.divideBy100) {
                    value = value / 100;
                }
                return this.formatNumber(value, this.getFormatString(format, formatOptions));
            }
            case FormatType.NUMBER: {
                let options = Object.assign({}, formatOptions);
                let formattedString = this.formatNumber(this.scaleValue(value, formatOptions.scale), this.getFormatString(format, options));
                if (options.abbreviated == "upper") {
                    return formattedString.toUpperCase();
                } else {
                    return formattedString;
                }
            }
            case FormatType.CURRENCY: {
                let formattedString;
                let numericalValue = numeral(value).value();
                let scaledValue = this.scaleValue(numericalValue, formatOptions.scale);
                const separators = this.getSeparatorsForCurrency(formatOptions.currency);

                if (formatOptions.accountingStyle) {
                    formattedString = numeral(scaledValue).format(this.getFormatString(format, formatOptions));

                    if (formatOptions.separator && formatOptions.currency) {
                        formattedString = this.setSeparatorsSymbols(formattedString, separators);
                    }
                } else {
                    let formattedValueAsNumber = numeral(scaledValue).format(this.getFormatString(FormatType.NUMBER, formatOptions));

                    if (formatOptions.separator && formatOptions.currency) {
                        formattedValueAsNumber = this.setSeparatorsSymbols(formattedValueAsNumber, separators);
                    }
                    formattedString = formattedValueAsNumber.startsWith("+") || formattedValueAsNumber.startsWith("-")
                        ? `${formattedValueAsNumber[0]}${formatOptions.currency}${formattedValueAsNumber.slice(1)}`
                        : `${formatOptions.currency}${formattedValueAsNumber}`;
                }

                return formatOptions.abbreviated === "upper" ? formattedString.toUpperCase() : formattedString;
            }
            case FormatType.DATE:
                return this.formatDate(value, this.getFormatString(format, formatOptions));
            default:
                return value;
        }
    },

    formatNumber(value, format) {
        return numeral(value).format(format);
    },

    formatDuration(duration, type = "seconds", format) {
        let m = moment.duration(duration, type);
        return moment.duration(duration, type).format(format);
    },

    formatDate(value, format) {
        switch (format) {
            case "none":
                return value;
            case "abbr-month":
                return moment(value).format("MMM").charAt(0);
            case "abbr-month-year":
                return moment(value).format("MMM").charAt(0) + " " + moment(value).format("YY");
            default:
                return moment(value).format(format);
        }
    },

    /**
     * Converts milliseconds to UNIX timestamp in seconds
     * @param {number} ms - milliseconds since epoch
     * @returns {number} timestamp in seconds
     */
    msToTimestamp(ms) {
        return Math.floor(ms / 1000);
    },

    formatTimestamp(time, words = false, raw = false) {
        const t = moment.unix(time);

        if (words || t.isBefore(moment().subtract(1, "w"))) {
            return t.format("MMM DD YYYY");
        } else if (raw) {
            return t.format("MM/DD/YYYY");
        } else {
            return t.fromNow();
        }
    },

    getCurrencies() {
        return SORTED_CURRENCIES;
    },

    detectFormatFromString(s) {
        let options = this.getDefaultFormatOptions();

        let currency;
        for (let c of this.getCurrencies()) {
            if (s.startsWith(c)) {
                currency = c;
            }
        }

        const parsedNumericValue = this.parseNumberString(s);

        const numeralValidate = numeral.validateEx ? numeral.validateEx(s) : numeral.validate(s);

        if (currency || numeralValidate || parsedNumericValue != null) {
            const normalizedNumericValue = this.normalizeNumericString(s);
            const decimalIndex = normalizedNumericValue?.indexOf(".") ?? -1;

            options.decimal = decimalIndex >= 0 ? (normalizedNumericValue.length - decimalIndex - 1) : 0;
            options.separator = true;

            if (currency) {
                options.currency = currency;
                options.accountingStyle = false;
                return {
                    format: FormatType.CURRENCY,
                    formatOptions: options
                };
            } else if (s.includes("%")) {
                return {
                    format: FormatType.PERCENT,
                    formatOptions: options
                };
            } else {
                return {
                    format: FormatType.NUMBER,
                    formatOptions: options
                };
            }
        } else if (moment(s, ACCEPTED_DATE_FORMATS, true).isValid()) {
            // parseFormat gives priority to "MM/YY" over "MM/DD" so we need to check for that first
            options.dateFormat = moment(s, "M/DD", true).isValid() ? "M/D" : parseFormat(s);

            return {
                format: FormatType.DATE,
                formatOptions: options
            };
        } else {
            return {
                format: FormatType.TEXT,
                formatOptions: options
            };
        }
    },

    getFormatString(format, formatOptions) {
        let formatString = "";

        if (format == FormatType.NUMBER || format == FormatType.CURRENCY) {
            formatString += "0";
            if (formatOptions.separator) {
                formatString += ",0";
            }
            if (formatOptions.decimal == 0) {
                // do nothing
            } else if (formatOptions.decimal > 0) {
                formatString += ".";
                for (let i = 0; i < formatOptions.decimal; i++) {
                    formatString += "0";
                }
            } else {
                // auto decimals
                formatString += ".[00]";
            }

            if (formatOptions.abbreviated) {
                formatString += "a";
            }

            if (formatOptions.accountingStyle) {
                formatString = "(" + formatString + ")";
            }
        }

        if (format == FormatType.PERCENT) {
            formatString += "0";

            if (formatOptions.decimal != 0 && formatOptions.decimal !== "auto") {
                formatString += ".";
                for (let i = 0; i < formatOptions.decimal; i++) {
                    formatString += "0";
                }
            }
            if (formatOptions.decimal === "auto") {
                formatString += ".[00]";
            }
            formatString += "%";
        }

        if (format == FormatType.NUMBER || format == FormatType.CURRENCY || format == FormatType.PERCENT) {
            if (formatOptions.changeStyle == CellChangeStyle.PLUS_MINUS) {
                formatString = "+" + formatString;
            }
        }

        if (format == "date") {
            formatString = formatOptions.dateFormat || "ll";
        }

        return formatString;
    },

    getFormatType(format) {
        switch (format) {
            case FormatType.NUMBER:
            case FormatType.CURRENCY:
            case FormatType.PERCENT:
                return "numeric";
            case FormatType.DATE:
                return "date";
            case FormatType.ICON:
                return "icon";
            default:
        }
    },

    getSeparatorsForCurrency(symbol) {
        const currencies = {
            "$": { thousands: ",", decimal: "." },
            "€": { thousands: ".", decimal: "," },
            "£": { thousands: ",", decimal: "." },
            "¥": { thousands: ",", decimal: "." },
            "₹": { thousands: ",", decimal: "." },
            "₽": { thousands: " ", decimal: "," },
            "kr": { thousands: " ", decimal: "," },
            "MX$": { thousands: ",", decimal: "." },
            "R$": { thousands: ".", decimal: "," },
            "₩": { thousands: ",", decimal: "." },
            "₪": { thousands: ",", decimal: "." },
            "₫": { thousands: ".", decimal: "," },
            "Rp": { thousands: ".", decimal: "," },
            "S$": { thousands: ",", decimal: "." }
        };

        return currencies[symbol] || { thousands: ",", decimal: "." };
    },

    parseCurrencySymbol(value) {
        if (!value || typeof value !== "string") {
            return null;
        }

        const trimmed = value.trim();

        const supportedSymbols = this.getCurrencies();
        if (supportedSymbols.includes(trimmed)) {
            return trimmed;
        }

        const letteredSymbolLookup = supportedSymbols.reduce((acc, sym) => {
            acc[sym.toLowerCase()] = sym;
            return acc;
        }, {});
        const lowerTrimmed = trimmed.toLowerCase();
        if (letteredSymbolLookup[lowerTrimmed]) {
            return letteredSymbolLookup[lowerTrimmed];
        }

        const code = trimmed.toUpperCase();

        const codeToSymbol = {
            "USD": "$",
            "EUR": "€",
            "GBP": "£",
            "JPY": "¥",
            "CNY": "¥",
            "CNH": "¥",
            "INR": "₹",
            "RUB": "₽",
            "NOK": "kr",
            "SEK": "kr",
            "DKK": "kr",
            "ISK": "kr",
            "MXN": "MX$",
            "BRL": "R$",
            "KRW": "₩",
            "ILS": "₪",
            "VND": "₫",
            "IDR": "Rp",
            "SGD": "S$"
        };

        return codeToSymbol[code] || null;
    }
};

export { formatter };
